import { connectImapMailbox, imapPresetQueries } from "@/components/connectors/api.ts";
import { reconnectButtonClass } from "@/components/connectors/ConnectorTile.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { apiErrorMessage } from "@/lib/http.ts";
import {
  type ImapPresetTile,
  imapLookupHintCopy,
  imapPresetGuardCopy,
} from "@/shared/connectors/imapConnectCopy.ts";
import { cn } from "@/lib/utils.ts";
import type { ConnectorSummary } from "@/types/connectors.ts";
import { type ConnectImapReq, IMAP_CONNECT_PATH, type ImapSecurity } from "@/types/imap.ts";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ExternalLink } from "lucide-react";
import { useEffect, useId, useState } from "react";

/** "default" keeps the preset's port and security mode. */
type EndpointFields = { host: string; port: string; security: ImapSecurity | "default" };

const emptyEndpoint = (): EndpointFields => ({ host: "", port: "", security: "default" });

const SECURITY_OPTIONS: readonly { value: EndpointFields["security"]; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "tls", label: "TLS" },
  { value: "starttls", label: "STARTTLS" },
];

const isSecurityOption = (value: string): value is EndpointFields["security"] =>
  SECURITY_OPTIONS.some((option) => option.value === value);

/** One transport's Advanced overrides; each field optional so one drifted value can be fixed alone. */
const EndpointOverrides = ({
  transport,
  value,
  onChange,
}: {
  transport: "IMAP" | "SMTP";
  value: EndpointFields;
  onChange: (next: EndpointFields) => void;
}) => {
  return (
    <fieldset className="grid grid-cols-[1fr_5rem_7.5rem] gap-2">
      <legend className="mb-1 text-xs font-medium text-muted-foreground">{transport} server</legend>
      <Input
        aria-label={`${transport} host`}
        placeholder={`${transport.toLowerCase()}.example.com`}
        autoComplete="off"
        value={value.host}
        onChange={(e) => onChange({ ...value, host: e.target.value })}
      />
      <Input
        aria-label={`${transport} port`}
        placeholder="Port"
        inputMode="numeric"
        value={value.port}
        onChange={(e) => onChange({ ...value, port: e.target.value.replace(/\D/g, "") })}
      />
      <Select
        value={value.security}
        onValueChange={(security) => {
          if (isSecurityOption(security)) onChange({ ...value, security });
        }}
      >
        <SelectTrigger aria-label={`${transport} security`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SECURITY_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </fieldset>
  );
};

const submitLabel = (pending: boolean, reconnecting: boolean): string => {
  if (pending) return "Checking with your mail servers…";
  return reconnecting ? "Reconnect" : "Connect";
};

const overrideFor = (fields: EndpointFields) => {
  const host = fields.host.trim();
  const override = {
    ...(host ? { host } : {}),
    ...(fields.port ? { port: Number(fields.port) } : {}),
    ...(fields.security !== "default" ? { security: fields.security } : {}),
  };
  return Object.keys(override).length ? override : undefined;
};

/**
 * Connect form for an IMAP/SMTP mailbox. Posts straight to the connect
 * endpoint so the app password never becomes a message. With `email` fixed it
 * reconnects that mailbox in place.
 *
 * With `preset` set the dialog belongs to one provider's tile: it leads with
 * that provider's setup steps, drops Advanced (the preset owns the servers),
 * and refuses an address from a different provider before anything is sent.
 * Without it — the "Other email inbox" tile, and Reconnect — the provider is
 * detected from the address, as it has always been.
 */
export const ImapConnectDialog = ({
  open,
  onOpenChange,
  submitPath,
  email: fixedEmail,
  preset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submitPath: string;
  email?: string;
  preset?: ImapPresetTile;
}) => {
  const queryClient = useQueryClient();
  // Looked up on blur: a half-typed address would flash "unknown provider".
  const [lookupEmail, setLookupEmail] = useState(fixedEmail);
  // Tracked separately from the looked-up address so a result can be held
  // against the text actually in the field — see `lookupIsCurrent`.
  const [typedEmail, setTypedEmail] = useState(fixedEmail ?? "");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const emailId = useId();
  const passwordId = useId();

  const hint = useQuery(
    imapPresetQueries.lookup(lookupEmail?.includes("@") ? lookupEmail : undefined),
  ).data;
  /**
   * Whether the answer on hand is about the address the user is looking at.
   *
   * The lookup only runs on blur, so between edits the result describes a
   * previous address. Saying "that looks like an AT&T address" under an address
   * the user has already corrected is worse than saying nothing, and on a preset
   * tile it also greys out Connect for a mailbox that would connect fine.
   */
  const lookupIsCurrent =
    (lookupEmail ?? "").trim().toLowerCase() === typedEmail.trim().toLowerCase();
  const usableHint = lookupIsCurrent ? hint : undefined;
  const guardText = usableHint && preset ? imapPresetGuardCopy(usableHint, preset) : undefined;
  const hintText = usableHint && !preset ? imapLookupHintCopy(usableHint) : undefined;

  // Only Advanced can connect an unknown provider, and only the fallback tile
  // has Advanced — a preset tile sends the user there instead.
  useEffect(() => {
    if (!preset && usableHint?.kind === "unknown_provider") setAdvancedOpen(true);
  }, [usableHint, preset]);

  const connect = useMutation({
    mutationFn: (req: ConnectImapReq) => connectImapMailbox(submitPath, req),
    // No success toast: the connected row is the confirmation. `currentUser`
    // carries the needsAuth flags behind the reauth banner.
    onSuccess: () => {
      for (const key of ["connectorCatalog", "connectors", "currentUser"]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      close();
    },
  });
  const error = connect.isError
    ? (apiErrorMessage(connect.error) ?? "Couldn't connect. Check the details and try again.")
    : null;

  const form = useForm({
    defaultValues: {
      email: fixedEmail ?? "",
      password: "",
      imap: emptyEndpoint(),
      smtp: emptyEndpoint(),
    },
    onSubmit: ({ value }) => {
      // A preset tile sends its preset and nothing else: the server checks the
      // address against it, and the schema refuses overrides alongside it.
      if (preset) {
        connect.mutate({
          email: value.email.trim(),
          password: value.password,
          presetId: preset.presetId,
        });
        return;
      }
      const advanced = { imap: overrideFor(value.imap), smtp: overrideFor(value.smtp) };
      connect.mutate({
        email: value.email.trim(),
        password: value.password,
        ...(advanced.imap || advanced.smtp ? { advanced } : {}),
      });
    },
  });

  // The dialog stays mounted while closed, so a rejected password and its
  // error would otherwise greet the next open.
  const close = () => {
    form.reset();
    connect.reset();
    setLookupEmail(fixedEmail);
    setTypedEmail(fixedEmail ?? "");
    setAdvancedOpen(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {fixedEmail
              ? "Reconnect mailbox"
              : `Connect ${preset ? preset.name : "an email inbox"}`}
          </DialogTitle>
          <DialogDescription>
            {preset
              ? `Your password is stored encrypted and sent only to ${preset.name}'s mail servers.`
              : "Works with any mailbox that supports IMAP. Use an app password, not your sign-in password. It is stored encrypted and sent only to your mail servers."}
          </DialogDescription>
        </DialogHeader>
        {preset && (
          <div className="space-y-2 rounded-md bg-muted/50 py-3 pl-8 pr-4">
            <ol className="list-decimal space-y-1.5 text-xs text-muted-foreground">
              {preset.setupSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <a
              href={preset.setupUrl}
              target="_blank"
              rel="noreferrer"
              className="-ml-4 inline-flex items-center gap-1 text-xs font-medium text-foreground underline underline-offset-2"
              data-statsig-name="connectors-imap-setup-link"
            >
              Open {preset.name}
              <ExternalLink className="size-3" />
            </a>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="space-y-3"
        >
          <form.Field name="email">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor={emailId}>Email address</Label>
                <Input
                  id={emailId}
                  type="email"
                  autoComplete="email"
                  required
                  disabled={fixedEmail !== undefined}
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    setTypedEmail(e.target.value);
                  }}
                  onBlur={() => {
                    field.handleBlur();
                    setLookupEmail(field.state.value.trim());
                  }}
                />
                {guardText && (
                  <p role="alert" className="text-xs text-destructive">
                    {guardText}
                  </p>
                )}
                {hintText && (
                  <p
                    className={cn(
                      "text-xs",
                      hint?.kind === "oauth_provider"
                        ? "text-destructive"
                        : "text-muted-foreground",
                    )}
                  >
                    {hintText}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <form.Field name="password">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor={passwordId}>{preset?.passwordLabel ?? "App password"}</Label>
                <Input
                  id={passwordId}
                  type="password"
                  autoComplete="off"
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>
          {/* The preset owns the servers, so its tile has nothing to override. */}
          {!preset && (
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2 gap-1 px-2"
                  data-statsig-name="connectors-imap-advanced-toggle"
                >
                  <ChevronDown
                    className={cn("size-3.5 transition-transform", advancedOpen && "rotate-180")}
                  />
                  Advanced
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                <form.Field name="imap">
                  {(field) => (
                    <EndpointOverrides
                      transport="IMAP"
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
                <form.Field name="smtp">
                  {(field) => (
                    <EndpointOverrides
                      transport="SMTP"
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
                <p className="text-xs text-muted-foreground">
                  Ports 143, 993, 465, and 587 only. Servers must belong to your address's domain or
                  its mail provider.
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="submit"
              size="sm"
              disabled={connect.isPending || Boolean(guardText)}
              data-statsig-name="connectors-imap-connect-submit"
            >
              {submitLabel(connect.isPending, Boolean(fixedEmail))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export type { ImapPresetTile };

/** A button that opens the connect form; with `email` fixed, it reconnects that mailbox. */
export const ImapConnectButton = ({
  submitPath,
  email,
  label,
  className,
  statsigName,
  preset,
}: {
  submitPath: string;
  email?: string;
  label: string;
  className?: string;
  statsigName: string;
  preset?: ImapPresetTile;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="primary"
        size="xs"
        className={className}
        onClick={() => setOpen(true)}
        data-statsig-name={statsigName}
      >
        {label}
      </Button>
      <ImapConnectDialog
        open={open}
        onOpenChange={setOpen}
        submitPath={submitPath}
        email={email}
        preset={preset}
      />
    </>
  );
};

/** The account row's Reconnect for an IMAP mailbox. */
export const ImapReconnectButton = ({ connector }: { connector: ConnectorSummary }) => (
  <ImapConnectButton
    submitPath={IMAP_CONNECT_PATH}
    email={connector.email ?? connector.displayName}
    label="Reconnect"
    className={reconnectButtonClass}
    statsigName="connectors-account-reconnect"
  />
);
